'use client';

import { useCallback, useRef } from 'react';
import bs58 from 'bs58';
import { getUserAccountPublicKeySync } from '@velocity-exchange/sdk';
import { usePrivy } from '@privy-io/react-auth';
import { usePrivyWalletContext } from 'src/providers/wallets/PrivyWalletProvider';
import { usePrivyEmbeddedWalletWithFallback } from 'src/hooks/wallets/privy/mappings/usePrivyEmbeddedWalletWithFallback';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { isWalletBlacklistedForEmbeddedWallets } from 'src/constants/wallets';
import { DEFAULT_PUBLIC_KEY } from 'src/@types/types';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';

const CACHE_TTL_MS = 55 * 60 * 1000; // 55 minutes (backend allows 1 hour)

export interface ApiAuthHeaders {
	'x-wallet-address': string;
	'x-signature': string;
	'x-signed-message': string;
}

interface CachedAuth {
	headers: ApiAuthHeaders;
	cacheKey: string;
	expiresAt: number;
}

// Module-level cache so it persists across re-renders and hook instances
let cachedAuth: CachedAuth | null = null;

export default function useApiAuth() {
	const { ready: privyReady, authenticated: privyAuthenticated } = usePrivy();
	const { embeddedWallet } = usePrivyWalletContext();
	const embeddedWalletPublicKey = usePrivyEmbeddedWalletWithFallback();
	const walletAdapter = useDriftStore((s) => s.wallet.current.adapter);
	const authority = walletAdapter?.publicKey;
	const driftProgramId = useDriftStore(
		(s) => s.driftClient.client?.program.programId
	);
	const currentUserAccount = useCurrentUserAccount();
	const signingRef = useRef<Promise<ApiAuthHeaders> | null>(null);

	const isBlacklisted = isWalletBlacklistedForEmbeddedWallets(
		walletAdapter?.name
	);

	const delegate = currentUserAccount?.delegate;
	const isDelegated = !!delegate && !delegate.equals(DEFAULT_PUBLIC_KEY);
	// When the account is delegated, wait for embeddedWalletPublicKey to load
	// before determining whether the delegate is the embedded wallet or not.
	// Without this, a cold cache (stale browser) causes isDelegatedToNonEmbedded
	// to be true prematurely while the DB mapping query is still in flight.
	const isDelegateCheckReady = !isDelegated || !!embeddedWalletPublicKey;

	const isDelegatedToEmbeddedWallet =
		!!delegate &&
		!!embeddedWalletPublicKey &&
		delegate.equals(embeddedWalletPublicKey);
	const isDelegatedToNonEmbedded =
		isDelegated && isDelegateCheckReady && !isDelegatedToEmbeddedWallet;

	const canUseEmbeddedWallet =
		!isBlacklisted && !isDelegatedToNonEmbedded && !!currentUserAccount;

	const getAuthHeaders = useCallback(async (): Promise<ApiAuthHeaders> => {
		if (!authority) {
			throw new Error('Wallet not connected');
		}

		const shouldUseEmbeddedWallet = canUseEmbeddedWallet && !!embeddedWallet;
		const walletAddress = shouldUseEmbeddedWallet
			? embeddedWallet.address
			: authority.toBase58();
		const driftAuthority = currentUserAccount?.authority ?? authority;

		const subAccountId =
			currentUserAccount?.client?.getUserAccount()?.subAccountId;
		const driftUserAccount =
			shouldUseEmbeddedWallet &&
			driftProgramId &&
			subAccountId !== undefined &&
			walletAddress !== driftAuthority.toBase58()
				? getUserAccountPublicKeySync(
						driftProgramId,
						driftAuthority,
						subAccountId
					).toString()
				: undefined;
		const delegateScope = driftUserAccount ?? 'direct';
		const cacheKey = `${walletAddress}:${delegateScope}`;

		if (
			cachedAuth &&
			cachedAuth.cacheKey === cacheKey &&
			Date.now() < cachedAuth.expiresAt
		) {
			return cachedAuth.headers;
		}

		if (signingRef.current) {
			return signingRef.current;
		}

		const signingPromise = (async () => {
			const messagePayload: Record<string, unknown> = {
				action: 'auth',
				ts: Math.floor(Date.now() / 1000),
				walletAddress,
			};

			if (driftUserAccount) {
				messagePayload.isDelegate = true;
				messagePayload.driftUserAccount = driftUserAccount;
			}

			const message = JSON.stringify(messagePayload);
			const messageBytes = new TextEncoder().encode(message);

			let signatureBytes: Uint8Array;
			if (shouldUseEmbeddedWallet) {
				const result = await embeddedWallet.signMessage({
					message: messageBytes,
				});
				signatureBytes = result.signature;
			} else {
				if (!walletAdapter?.signMessage) {
					throw new Error('Wallet does not support message signing');
				}
				signatureBytes = await walletAdapter.signMessage(messageBytes);
			}

			const signatureBs58 = bs58.encode(signatureBytes);

			const headers: ApiAuthHeaders = {
				'x-wallet-address': walletAddress,
				'x-signature': signatureBs58,
				'x-signed-message': message,
			};

			cachedAuth = {
				headers,
				cacheKey,
				expiresAt: Date.now() + CACHE_TTL_MS,
			};

			return headers;
		})();

		signingRef.current = signingPromise;

		try {
			return await signingPromise;
		} finally {
			signingRef.current = null;
		}
	}, [
		embeddedWallet,
		walletAdapter,
		authority,
		driftProgramId,
		currentUserAccount,
		canUseEmbeddedWallet,
	]);

	// Non-blacklisted users: only connected once embedded wallet is loaded.
	// Blacklisted users: connected via wallet popup.
	const isConnected = canUseEmbeddedWallet
		? !!embeddedWallet
		: privyReady && !!walletAdapter?.signMessage;

	// True when user should enable signless trading before API auth can work.
	// Gate on !privyAuthenticated so already-authenticated users don't see a
	// flash while the embedded wallet is still loading after page refresh.
	const needsSignlessSetup =
		privyReady &&
		canUseEmbeddedWallet &&
		!embeddedWallet &&
		!privyAuthenticated;

	return { getAuthHeaders, isConnected, needsSignlessSetup };
}
